import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux';
import {
  fetchStripeProducts,
  fetchStripeProductById,
  setSelectedProduct,
  clearError,
  clearProducts,
  selectStripeProducts,
  selectSelectedStripeProduct,
  selectStripeLoading,
  selectStripeError,
  selectStripeStatus,
  selectStripeLastFetched,
} from '../../redux/stripe/stripeSlice';
import { StripeProduct } from '../../types/stripe';

export interface UseStripeProductsOptions {
  autoFetch?: boolean;
  activeOnly?: boolean;
  limit?: number;
  refetchInterval?: number; // Refetch interval in milliseconds
}

export const useStripeProducts = (options: UseStripeProductsOptions = {}) => {
  const { autoFetch = false, activeOnly = true, limit, refetchInterval } = options;
  const dispatch = useDispatch<AppDispatch>();

  // Selectors
  const products = useSelector(selectStripeProducts);
  const selectedProduct = useSelector(selectSelectedStripeProduct);
  const isLoading = useSelector(selectStripeLoading);
  const error = useSelector(selectStripeError);
  const status = useSelector(selectStripeStatus);
  const lastFetched = useSelector(selectStripeLastFetched);

  // Fetch products
  const fetch = useCallback(
    async (params?: { active?: boolean; limit?: number }) => {
      const fetchParams = {
        active: params?.active ?? activeOnly,
        limit: params?.limit ?? limit,
      };
      const result = await dispatch(fetchStripeProducts(fetchParams)).unwrap();
      return result;
    },
    [dispatch, activeOnly, limit]
  );

  // Fetch product by ID
  const fetchById = useCallback(
    async (productId: string) => {
      const result = await dispatch(fetchStripeProductById(productId)).unwrap();
      return result;
    },
    [dispatch]
  );

  // Select a product
  const select = useCallback(
    (product: StripeProduct | null) => {
      dispatch(setSelectedProduct(product));
    },
    [dispatch]
  );

  // Clear error
  const clear = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Clear all products
  const clearAll = useCallback(() => {
    dispatch(clearProducts());
  }, [dispatch]);

  // Get product by ID from current state
  const getById = useCallback(
    (id: string): StripeProduct | undefined => {
      return products.find((p) => p.id === id);
    },
    [products]
  );

  // Get products by name
  const getByName = useCallback(
    (name: string): StripeProduct[] => {
      return products.filter((p) =>
        p.name.toLowerCase().includes(name.toLowerCase())
      );
    },
    [products]
  );

  // Get active products
  const getActiveProducts = useCallback((): StripeProduct[] => {
    return products.filter((p) => p.active);
  }, [products]);

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch) {
      fetch();
    }
  }, [autoFetch, fetch]);

  // Refetch interval
  useEffect(() => {
    if (refetchInterval && refetchInterval > 0) {
      const interval = setInterval(() => {
        fetch();
      }, refetchInterval);

      return () => clearInterval(interval);
    }
  }, [refetchInterval, fetch]);

  return {
    // Data
    products,
    selectedProduct,
    activeProducts: getActiveProducts(),
    lastFetched,

    // Loading states
    isLoading,
    status,

    // Error
    error,

    // Actions
    fetch,
    fetchById,
    select,
    clear,
    clearAll,
    getById,
    getByName,

    // Helpers
    hasProducts: products.length > 0,
    productCount: products.length,
    activeProductCount: getActiveProducts().length,
  };
};

